import { localizePathname, type Locale } from '@/lib/i18n-routes';
import { buildBackendFilters, normalizeFilterParams, readURLFilters, searchURLParams } from '@/lib/search-filters';
import type { FilterParams } from '@/lib/search-filter-params';
import { filterParamsToParsed } from '@/lib/seo-lists/filter-adapter';
import type { CompanySearchResponse } from '@/lib/actions/company-search';
import type { NaceCode } from './searchPageTypes';

export function filtersToSearchParams(
  filters: FilterParams,
  naceCodes: NaceCode[],
  overrides: {
    sort: string;
    offset: number;
    limit: number;
  },
) {
  return buildBackendFilters({ ...filterParamsToParsed(filters), ...overrides }, { naceCodes });
}

/**
 * The filters behind the rendered search: URL params win, then the preset a
 * /listen/[slug] page supplies, then the empty default. What "the URL sets a
 * filter" means per kind is `readURLFilters`' rule (lib/search-filters.ts).
 */
export function buildInitialFilters(
  searchParams: URLSearchParams,
  presetFilters: Partial<FilterParams> | undefined,
  listSlug: string | undefined,
  pathname: string,
  locale: Locale,
): FilterParams {
  const preset = normalizeFilterParams(listSlug && pathname === localizePathname('/search', locale as Locale) ? undefined : presetFilters);
  return readURLFilters(searchURLParams(searchParams), preset);
}

/** How many filter values a submitted search carries (search_performed event). */
export { submittedFilterCount } from '@/lib/search-filter-defs';

/** The /api/log/export payload: the export stage plus the filters behind the results. */
export function exportLogBody(
  stage: 'click' | 'completed',
  extra: Record<string, unknown> | undefined,
  url: string,
  canExport: boolean,
  searchParams: URLSearchParams,
  initialFilters: FilterParams,
  currentSort: string,
  searchResponse: Pick<CompanySearchResponse, 'total'>,
) {
  const body = {
    stage,
    canExport,
    keyword: searchParams.get('q') || undefined,
    city: searchParams.getAll('city').join(',') || undefined,
    bundesland: searchParams.getAll('Bundesland').join(',') || undefined,
    revenueMin: initialFilters.revenueMin,
    revenueMax: initialFilters.revenueMax,
    aktivaMin: initialFilters.aktivaMin,
    aktivaMax: initialFilters.aktivaMax,
    profitMin: initialFilters.profitMin,
    profitMax: initialFilters.profitMax,
    employeesMin: initialFilters.employeesMin,
    employeesMax: initialFilters.employeesMax,
    naceCodes: searchParams.getAll('naceCode').join(',') || undefined,
    industries: searchParams.getAll('industry').join(',') || undefined,
    tags: searchParams.getAll('tag').join(',') || undefined,
    personName: searchParams.get('person_name') || undefined,
    foundedAtFrom: searchParams.get('foundedAtFrom') || undefined,
    foundedAtTo: searchParams.get('foundedAtTo') || undefined,
    sort: currentSort,
    resultCount: searchResponse.total,
    url,
    ...extra,
  };
  return body;
}
